/**
 * useSystemDate hook
 *
 * Fetches the authoritative system date for a client and caches it in local state.
 * Uses a module-level cache so that SPA navigation between pages returns the
 * cached value instantly (no API call needed on 2nd+ visit in the same session).
 *
 * Exposes a `refetch` function so that the useSystemDateRollover hook (Pause 5)
 * can trigger a re-fetch when a `system_date_rollover_warning` SSE event is received.
 *
 * Does not poll — the system date changes at most once per day.
 */

import { useState, useEffect, useCallback } from 'react';
import { SystemDateInfo } from '../types/systemDate';
import { systemDateService } from '../services/api/systemDateService';

interface UseSystemDateResult extends Partial<SystemDateInfo> {
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Module-level cache: survives SPA navigation, cleared on full page refresh.
// Keyed by clientId. The system date changes at most once per day, so stale
// data risk is negligible — and refetch() updates the cache when called.
const systemDateCache = new Map<string, SystemDateInfo>();

export function useSystemDate(clientId: string | undefined): UseSystemDateResult {
  const cached = clientId ? systemDateCache.get(clientId) : undefined;
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SystemDateInfo | null>(cached || null);

  const fetchDate = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    // Only show loading spinner if we have no cached data
    if (!systemDateCache.has(clientId)) {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await systemDateService.getSystemDate(clientId);
      if (response.success && response.data) {
        systemDateCache.set(clientId, response.data);
        setData(response.data);
      } else {
        setError(response.message || 'Failed to load system date');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load system date');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchDate();
  }, [fetchDate]);

  return {
    loading,
    error,
    refetch: fetchDate,
    ...data,
  };
}
